import toast from 'react-hot-toast';

class TokenManager {
  constructor() {
    this.tokenKey = 'google_token';
    this.tokenExpiryKey = 'google_token_expiry';
    this.refreshTimer = null;
  }

  // Parse JWT to get expiration time
  parseJwt(token) {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (e) {
      console.error('Failed to parse JWT:', e);
      return null;
    }
  }

  // Save token with expiry
  saveToken(token) {
    const payload = this.parseJwt(token);
    if (payload && payload.exp) {
      // Google ID tokens expire in 1 hour (3600 seconds)
      // Store expiry time in milliseconds
      const expiryTime = payload.exp * 1000;
      localStorage.setItem(this.tokenKey, token);
      localStorage.setItem(this.tokenExpiryKey, expiryTime.toString());
      
      // Set up auto-refresh 5 minutes before expiry
      this.setupAutoRefresh(expiryTime);
      
      return true;
    }
    return false;
  }

  // Get token if valid
  getToken() {
    const token = localStorage.getItem(this.tokenKey);
    const expiry = localStorage.getItem(this.tokenExpiryKey);
    
    if (!token || !expiry) {
      return null;
    }

    const expiryTime = parseInt(expiry);
    const currentTime = Date.now();
    
    // Check if token is expired (with 1 minute buffer)
    if (currentTime >= expiryTime - 60000) {
      this.clearToken();
      return null;
    }
    
    return token;
  }

  // Check if token is about to expire (within 5 minutes)
  isTokenExpiringSoon() {
    const expiry = localStorage.getItem(this.tokenExpiryKey);
    if (!expiry) return true;
    
    const expiryTime = parseInt(expiry);
    const currentTime = Date.now();
    const fiveMinutes = 5 * 60 * 1000;
    
    return (expiryTime - currentTime) <= fiveMinutes;
  }

  // Clear token and timer
  clearToken() {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.tokenExpiryKey);
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  // Set up auto-refresh timer
  setupAutoRefresh(expiryTime) {
    // Clear existing timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    const currentTime = Date.now();
    const timeUntilRefresh = expiryTime - currentTime - (5 * 60 * 1000); // 5 minutes before expiry
    
    if (timeUntilRefresh > 0) {
      this.refreshTimer = setTimeout(() => {
        this.handleTokenRefresh();
      }, timeUntilRefresh);
    }
  }

  // Handle token refresh
  handleTokenRefresh() {
    // Clear current token
    this.clearToken();
    
    // Notify user to re-authenticate
    toast.error('Your session has expired. Please sign in again.', {
      duration: 5000,
    });
    
    // Trigger re-authentication by reloading
    // This will cause the app to show the login screen
    setTimeout(() => {
      window.location.reload();
    }, 2000);
  }

  // Check token validity on API calls
  async validateToken() {
    const token = this.getToken();
    if (!token) {
      return { isValid: false, reason: 'No token found' };
    }

    // Check expiry
    if (this.isTokenExpiringSoon()) {
      return { isValid: false, reason: 'Token expiring soon' };
    }

    return { isValid: true, token };
  }
}

// Create singleton instance
const tokenManager = new TokenManager();

export default tokenManager;